import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { AppState } from '../../../store';
import {
  startCall,
  acceptCall,
  rejectCall,
  endCall,
} from '../../../store/calls/callsApi';
import {
  endActiveCall,
  setActiveCallCreds,
  setActiveCallPhase,
  subscribeCallStatus,
  unsubscribeCallStatus,
} from '../../../store/calls/actionCreators';
import { JanusCallSession } from '../../../store/calls/janusCallSession';
import { janusConnect } from '../../../store/calls/janusAdapter';

// Linger after a call ends so the outcome (Declined / No answer / …) is visible.
const END_LINGER_MS = 2500;

const errorMessage = (err: unknown): string => {
  const s = String(
    (err as { message?: string })?.message ?? (err as string) ?? '',
  );
  if (/notallowed|permission|denied/i.test(s)) {
    return 'Camera/microphone access was denied. Check browser permissions.';
  }
  if (/notfound|notreadable|device/i.test(s)) {
    return 'No camera/microphone available.';
  }
  return 'Could not connect the call. Please try again.';
};

// In-page v2 call controller (OI-1564). Drives the redux activeCall through its
// lifecycle: outgoing → REST start → Janus publish/subscribe; incoming → REST
// accept → Janus. Owns the (non-serializable) JanusCallSession + media streams;
// redux holds the serializable call metadata so the button + global modal align.
export function useJanusCall() {
  const dispatch = useDispatch();
  const activeCall = useSelector((s: AppState) => s.calls.activeCall);
  const selfCustomerId = useSelector(
    (s: AppState) => s.dashboardUser.dashboardUserInfo?.customer_id ?? null,
  );

  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const sessionRef = useRef<JanusCallSession | null>(null);
  const restStartedRef = useRef(false);
  const endTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeCallRef = useRef(activeCall);
  activeCallRef.current = activeCall;

  // Stop local media + Janus session (no redux change).
  const stopMedia = useCallback(() => {
    sessionRef.current?.end();
    sessionRef.current = null;
    restStartedRef.current = false;
    setLocalStream(null);
    setRemoteStream(null);
  }, []);

  // Remove the call from the store (and drop its bus subscription).
  const clearCall = useCallback(() => {
    if (endTimerRef.current) {
      clearTimeout(endTimerRef.current);
      endTimerRef.current = null;
    }
    const call = activeCallRef.current;
    if (call?.chatId) dispatch(unsubscribeCallStatus(call.chatId));
    setErrorMsg(null);
    dispatch(endActiveCall());
  }, [dispatch]);

  // User-initiated hang up — tell REST, then close immediately.
  const hangup = useCallback(() => {
    const call = activeCallRef.current;
    if (call?.callId) endCall(call.callId).catch((e) => console.error(e));
    stopMedia();
    clearCall();
  }, [stopMedia, clearCall]);

  // Reject an incoming call before answering.
  const reject = useCallback(() => {
    const call = activeCallRef.current;
    if (call?.callId) rejectCall(call.callId).catch((e) => console.error(e));
    stopMedia();
    clearCall();
  }, [stopMedia, clearCall]);

  useEffect(() => {
    if (!activeCall || selfCustomerId == null) return;

    // Peer/server ended it (terminal bus status) → stop media now, but leave the
    // card up briefly so the outcome shows, then clear.
    if (activeCall.phase === 'ended') {
      stopMedia();
      if (!endTimerRef.current) {
        endTimerRef.current = setTimeout(clearCall, END_LINGER_MS);
      }
      return;
    }

    // Outgoing: REST start once to mint room + token.
    if (
      activeCall.direction === 'outgoing' &&
      activeCall.phase === 'requesting' &&
      !restStartedRef.current
    ) {
      restStartedRef.current = true;
      startCall(activeCall.peerCustomerId)
        .then((resp) => {
          dispatch(setActiveCallCreds(resp));
          dispatch(subscribeCallStatus(resp.chat_id));
        })
        .catch((e) => {
          console.error('startCall failed', e);
          setErrorMsg(errorMessage(e));
        });
      return;
    }

    // Incoming: REST accept once (creds already came in the incoming payload).
    if (activeCall.direction === 'incoming' && !restStartedRef.current) {
      restStartedRef.current = true;
      if (activeCall.callId) {
        acceptCall(activeCall.callId).catch((e) =>
          console.error('acceptCall failed', e),
        );
      }
      if (activeCall.chatId) dispatch(subscribeCallStatus(activeCall.chatId));
    }

    // Have Janus creds + no session yet → start media.
    if (
      !sessionRef.current &&
      activeCall.janusUrl &&
      activeCall.roomId != null &&
      activeCall.token &&
      activeCall.callId != null
    ) {
      const session = new JanusCallSession({
        call: {
          call_id: activeCall.callId,
          chat_id: activeCall.chatId ?? '',
          janus_url: activeCall.janusUrl,
          room_id: activeCall.roomId,
          token: activeCall.token,
        },
        selfCustomerId,
        connect: janusConnect,
        onLocalStream: setLocalStream,
        onRemoteStream: (s) => {
          setRemoteStream(s);
          dispatch(setActiveCallPhase('in_call'));
        },
        onError: (err) => {
          console.error('janus session error', err);
          setErrorMsg(errorMessage(err));
        },
      });
      sessionRef.current = session;
      session.start();
      if (activeCall.phase !== 'in_call') {
        dispatch(setActiveCallPhase('connecting'));
      }
    }
  }, [activeCall, selfCustomerId, dispatch, stopMedia, clearCall]);

  // Safety net: stop the session + timer if the component unmounts mid-call.
  useEffect(
    () => () => {
      sessionRef.current?.end();
      if (endTimerRef.current) clearTimeout(endTimerRef.current);
    },
    [],
  );

  return { activeCall, localStream, remoteStream, errorMsg, hangup, reject };
}
